import React, { useRef, useEffect } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { useTexture, Html } from '@react-three/drei'
import * as THREE from 'three'

// Use sRGB colorspace for textures
THREE.Texture.DEFAULT_COLOR_SPACE = THREE.SRGBColorSpace

const ALBUM_SPACING = 8

/**
 * MusicOverlay Component
 * Renders album covers as image planes (like workOverlay renders project images)
 */
const MusicOverlay = ({
  tracks,
  currentItemIndex,
  onAlbumClick,
  focusedIndex,
  setFocusedIndex
}) => {
  const { camera } = useThree()
  const sceneGroupRef = useRef()
  const targetScenePosition = useRef(new THREE.Vector3(0, 0, 0))
  const currentScenePosition = useRef(new THREE.Vector3(0, 0, 0))

  console.log('MusicOverlay rendering with', tracks.length, 'tracks')
  console.log('Track album covers:', tracks.map(t => t.albumCover))

  // Set camera to look at the scene
  useEffect(() => {
    camera.lookAt(0, 0, 0)
  }, [camera])

  // Calculate target position based on current index
  useEffect(() => {
    const targetX = -currentItemIndex * ALBUM_SPACING
    targetScenePosition.current.set(targetX, 0, 0)
  }, [currentItemIndex])

  // Smooth scene movement animation
  useFrame(() => {
    if (sceneGroupRef.current) {
      currentScenePosition.current.lerp(targetScenePosition.current, 0.1)
      sceneGroupRef.current.position.copy(currentScenePosition.current)
    }
  })

  return (
    <>
      {/* Hemisphere light creates gradient background effect */}
      <hemisphereLight 
        skyColor="#4b6cb7" 
        groundColor="#182848" 
        intensity={2}
      />
      
      {/* Scene group for all albums */}
      <group ref={sceneGroupRef}>
        {tracks.map((track, index) => (
          <AlbumPlane
            key={track.id}
            track={track}
            index={index}
            position={[index * ALBUM_SPACING, 0, 0]}
            isFocused={focusedIndex === index}
            onClick={() => onAlbumClick(index)}
            onPointerOver={() => setFocusedIndex(index)}
            onPointerOut={() => setFocusedIndex(null)}
          />
        ))}
      </group>
    </>
  )
}

/**
 * AlbumPlane Component
 * Single album cover as an image plane (positioned at ground level, camera looks down)
 */
const AlbumPlane = ({ track, index, position, isFocused, onClick, onPointerOver, onPointerOut }) => {
  const meshRef = useRef()
  const [isHovered, setIsHovered] = React.useState(false)
  const { size } = useThree()
  
  // Load texture with error handling callback
  const texture = useTexture(
    track.albumCover,
    (loadedTexture) => {
      console.log(`✓ Loaded texture for ${track.title}`)
    },
    (err) => {
      console.error(`✗ Failed to load texture for ${track.title}:`, err)
    }
  )
  
  useEffect(() => {
    if (texture) {
      texture.colorSpace = THREE.SRGBColorSpace
      texture.needsUpdate = true
      console.log(`Texture applied for ${track.title}`)
    }
  }, [texture, track.title])

  // Dynamic album cover size based on window width
  const windowWidth = size.width
  let albumSize
  if (windowWidth < 480) {
    // Mobile phones: smaller albums
    albumSize = 5.0
  } else if (windowWidth < 768) {
    // Small tablets: medium albums
    albumSize = 6.0
  } else {
    // Tablets and desktop: larger albums
    albumSize = 7.0
  }
  
  const handlePointerOver = (e) => {
    e.stopPropagation()
    document.body.style.cursor = 'pointer'
    setIsHovered(true)
    onPointerOver()
  }

  const handlePointerOut = (e) => {
    e.stopPropagation()
    document.body.style.cursor = 'default'
    setIsHovered(false)
    onPointerOut()
  }

  const handleClick = (e) => {
    e.stopPropagation()
    onClick()
  }

  // Dynamic font size based on window width
  let fontSize = 48
  if (windowWidth < 480) {
    fontSize = 24 // Mobile phones
  } else if (windowWidth < 768) {
    fontSize = 28 // Small tablets
  } else if (windowWidth < 1440) {
    fontSize = 36 // Tablets
  }

  return (
    <group position={position}>
      {/* Album cover plane at ground level (Y=0), camera looks down from [0,15,0] */}
      <mesh
        ref={meshRef}
        position={[0, 0, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
        onClick={handleClick}
      >
        <planeGeometry args={[albumSize, albumSize]} />
        {texture ? (
          <meshBasicMaterial 
            map={texture} 
            transparent
            side={THREE.DoubleSide}
          />
        ) : (
          <meshBasicMaterial color="#666666" />
        )}
      </mesh>

      {/* Title and Artist label underneath album (3 units below) */}
      <Html
        position={[0, 0, 4.5]} // Slightly below album
        center
        distanceFactor={8}
        style={{
          pointerEvents: 'none',
          userSelect: 'none'
        }}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
          minWidth: `${windowWidth * 1}px`,
          maxWidth: `${windowWidth * 0.8}px`
        }}>
          <p className="info-cell info-cell-header" style={{ margin: 0, fontSize: `${fontSize}px`, fontWeight: '600' }}>
            {track.title}
          </p>
          <p className="info-cell" style={{ margin: 0, fontSize: `${fontSize}px`, fontWeight: '400' }}>
            {track.artist}
          </p>
        </div>
      </Html>
    </group>
  )
}

export default MusicOverlay
